function getFocusableElements(container) {
  const elements = Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  )

  return elements.filter(
    (element) =>
      !!(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length
      )
  )
}

// Shopify Forms has no hidden fields, so form 1180351 gets optional text fields with these
// labels in the Forms app; they're hidden here and filled from the callback button's data.
const PRODUCT_FIELDS = {
  product: (product) => product.title,
  variant: (product) => product.variantTitle,
}

function parseJSON(value) {
  try {
    return JSON.parse(value)
  } catch {
    return []
  }
}

function getProductData(trigger) {
  if (!trigger?.dataset.productTitle) return null

  const ids = parseJSON(trigger.dataset.variantIds || '[]').map(String)
  const titles = parseJSON(trigger.dataset.variantTitles || '[]')
  const selectedId =
    trigger
      .closest('[data-product-card-selected_variant_id]')
      ?.getAttribute('data-product-card-selected_variant_id') ||
    new URL(window.location.href).searchParams.get('variant') ||
    ids[0]
  const index = ids.indexOf(String(selectedId))

  return {
    title: trigger.dataset.productTitle,
    variantTitle: index > -1 ? titles[index] : '',
  }
}

function getFieldLabel(control, root) {
  const labelledBy = control.getAttribute('aria-labelledby')?.split(' ')[0]
  const text =
    control.labels?.[0]?.textContent ||
    (labelledBy && root.getElementById(labelledBy)?.textContent) ||
    control.getAttribute('aria-label') ||
    control.placeholder ||
    ''
  return text.replace(/\(.*?\)|\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

// Hide the largest wrapper that holds only this field (its label included)
function hideField(control) {
  let wrapper = control
  while (
    wrapper.parentElement &&
    wrapper.parentElement.querySelectorAll('input:not([type=hidden]), textarea, select').length === 1 &&
    !wrapper.parentElement.querySelector('button')
  )
    wrapper = wrapper.parentElement

  wrapper.style.setProperty('display', 'none', 'important')
  wrapper.setAttribute('aria-hidden', 'true')
  control.tabIndex = -1
}

// Set through the native setter so the Forms app's framework picks the value up. `force`
// re-sends it even when the input already shows it, in case Forms' own state was rebuilt.
function setFieldValue(control, value, force = false) {
  if (!force && control.value === value) return
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(control), 'value').set.call(control, value)
  control.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
  control.dispatchEvent(new Event('change', { bubbles: true, composed: true }))
}

function fillProductFields(root, product, force = false) {
  for (const control of root.querySelectorAll('input:not([type=hidden]), textarea')) {
    const getValue = PRODUCT_FIELDS[getFieldLabel(control, root)]
    if (!getValue) continue
    hideField(control)
    setFieldValue(control, getValue(product) || '', force)
  }
}

class CallbackFormModal extends window.HTMLElement {
  constructor() {
    super()
    this.onFocusIn = this.onFocusIn.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onEditorSelect = this.onEditorSelect.bind(this)
    this.onEditorDeselect = this.onEditorDeselect.bind(this)
  }

  connectedCallback() {
    if (this.hasAttribute('data-open-in-editor') && window.Shopify?.designMode) {
      document.addEventListener('shopify:section:load', this.onEditorSelect)
      document.addEventListener('shopify:section:select', this.onEditorSelect)
      document.addEventListener('shopify:block:select', this.onEditorSelect)
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect)
    }

    if (this.initialized) return
    this.initialized = true

    this.panel = this.querySelector('[data-callback-panel]')
    this.form = this.querySelector('form')

    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.closest('[data-callback-close]'))
        this.close()
    })
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.close()
    })

    this.form?.addEventListener('submit', this.onSubmit.bind(this))
    // Reset the busy state if the page comes back from the bfcache
    window.addEventListener('pageshow', () => this.setSubmitting(false))

    // Every contact form on the page gets the post result, so only reopen the one that was submitted
    const { hash } = window.location
    if (this.querySelector('[data-callback-state]') && (!hash || hash === `#${this.form?.id}`)) {
      this.open()
      this.cleanUrl()
    }
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.onEditorSelect)
    document.removeEventListener('shopify:section:select', this.onEditorSelect)
    document.removeEventListener('shopify:block:select', this.onEditorSelect)
    document.removeEventListener('shopify:section:deselect', this.onEditorDeselect)
    this.close({ restoreFocus: false })
  }

  isOpen() {
    return this.classList.contains('active-modal-popup')
  }

  open(opener = null) {
    this.opener = opener || document.querySelector(`[aria-controls="${this.id}"]`)
    this.startProductFieldSync()
    this.classList.add('active-modal-popup')
    document.body.classList.add('!overflow-hidden')
    document.addEventListener('focusin', this.onFocusIn)
    document.addEventListener('keydown', this.onKeyDown)
      ; (this.querySelector('[aria-invalid="true"]') || this.panel).focus()
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen()) return
    this.classList.remove('active-modal-popup')
    document.body.classList.remove('!overflow-hidden')
    document.removeEventListener('focusin', this.onFocusIn)
    document.removeEventListener('keydown', this.onKeyDown)
    this.stopProductFieldSync()
    if (restoreFocus) this.opener?.focus()
  }

  startProductFieldSync() {
    this.stopProductFieldSync()
    this.product = getProductData(this.opener)
    if (!this.product) return
    this.fieldWatchers = new Map()
    // The Forms embed can be added after the popup opens (its script is large and loads late)
    this.embedObserver = new MutationObserver(() => this.syncProductFields())
    this.embedObserver.observe(this, { childList: true, subtree: true })
    this.syncProductFields()
  }

  // Re-run while open: the Forms embed renders into its shadow root asynchronously and may re-render
  syncProductFields() {
    if (!this.fieldWatchers) return
    const roots =Array.from(this.querySelectorAll('*'), (element) => element.shadowRoot).filter(Boolean)

    for (const root of roots) {
      fillProductFields(root, this.product)
      if (this.fieldWatchers.has(root)) continue

      const observer = new MutationObserver(() => fillProductFields(root, this.product))
      observer.observe(root, { childList: true, subtree: true })

      // Capture phase runs before Forms' own submit handler reads its values
      const onSubmitIntent = (event) => {
        if (event.type === 'click' && !event.target.closest?.('button')) return
        fillProductFields(root, this.product, true)
      }
      root.addEventListener('submit', onSubmitIntent, true)
      root.addEventListener('click', onSubmitIntent, true)

      this.fieldWatchers.set(root, () => {
        observer.disconnect()
        root.removeEventListener('submit', onSubmitIntent, true)
        root.removeEventListener('click', onSubmitIntent, true)
      })
    }
  }

  stopProductFieldSync() {
    this.embedObserver?.disconnect()
    this.embedObserver = null
    this.fieldWatchers?.forEach((cleanup) => cleanup())
    this.fieldWatchers = null
  }

  onKeyDown(event) {
    if (event.key === 'Tab') this.tabbingBackward = event.shiftKey
  }

  onFocusIn(event) {
    if (this.contains(event.target)) return
    const focusable = getFocusableElements(this.panel)
    const target = this.tabbingBackward
      ? focusable[focusable.length - 1]
      : focusable[0]
      ; (target || this.panel).focus()
  }

  onEditorSelect(event) {
    if (event.target.contains(this) || this.contains(event.target)) this.open()
  }

  onEditorDeselect(event) {
    if (event.target.contains(this)) this.close({ restoreFocus: false })
  }

  onSubmit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }
    this.setSubmitting(true)
  }

  setSubmitting(submitting) {
    this.submitting = submitting
    const submitButton = this.form?.querySelector('[type="submit"]')
    if (!submitButton) return
    submitting
      ? submitButton.setAttribute('aria-busy', 'true')
      : submitButton.removeAttribute('aria-busy')
  }

  // Drop the contact form redirect params so a refresh doesn't reopen the modal
  cleanUrl() {
    const url = new URL(window.location.href)
    for (const key of [...url.searchParams.keys()]) {
      if (key === 'contact_posted' || key === 'form_type' || key.startsWith('contact['))
        url.searchParams.delete(key)
    }
    if (this.form && url.hash === `#${this.form.id}`) url.hash = ''
    window.history.replaceState(window.history.state, '', url)
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[aria-controls^="CallbackModal-"]')
  if (!trigger) return
  document.getElementById(trigger.getAttribute('aria-controls'))?.open(trigger)
})

window.customElements.define('callback-form-modal', CallbackFormModal)